class WhamHome {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-wham-track]');
    this.slides = Array.from(root.querySelectorAll('[data-wham-slide]'));
    this.dotsHost = root.querySelector('[data-wham-dots]');
    this.backgroundImage = root.querySelector('.wham-home__background-image');
    this.defaultIndex = this.getDefaultIndex();
    this.activeIndex = this.defaultIndex;
    this.motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.rafId = null;
    this.initialPositionApplied = false;

    if (!this.track || this.slides.length === 0) return;

    this.renderDots();
    this.bindCarousel();
    this.setActive(this.defaultIndex);
    this.queueInitialPosition();
  }

  getDefaultIndex() {
    const defaultIndex = this.slides.findIndex((slide) => slide.hasAttribute('data-wham-default-slide'));
    return defaultIndex >= 0 ? defaultIndex : 0;
  }

  renderDots() {
    if (!this.dotsHost) return;

    this.dotsHost.innerHTML = '';
    this.dots = this.slides.map((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wham-home__dot';
      button.setAttribute('aria-label', `Go to slide ${index + 1}`);
      button.dataset.index = String(index);
      button.addEventListener('click', () => {
        this.scrollToSlide(index);
      });
      this.dotsHost.appendChild(button);
      return button;
    });
  }

  bindCarousel() {
    this.track.addEventListener(
      'scroll',
      () => {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.syncActiveToViewport();
        });
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      () => {
        if (this.initialPositionApplied) {
          this.syncActiveToViewport();
          return;
        }

        this.queueInitialPosition();
      },
      { passive: true }
    );
    window.addEventListener('load', () => this.queueInitialPosition(), { once: true });
  }

  queueInitialPosition() {
    if (this.defaultIndex < 0 || this.initialPositionApplied) return;

    requestAnimationFrame(() => {
      if (this.initialPositionApplied) return;

      this.scrollToSlide(this.defaultIndex, { behavior: 'auto' });
      this.setActive(this.defaultIndex);
      this.initialPositionApplied = true;
    });
  }

  scrollToSlide(index, { behavior } = {}) {
    const slide = this.slides[index];
    if (!slide) return;

    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const nextLeft =
      this.track.scrollLeft +
      (slideRect.left - trackRect.left) -
      (trackRect.width - slideRect.width) / 2;

    this.track.scrollTo({
      left: nextLeft,
      behavior: behavior ?? (this.motionReduced.matches ? 'auto' : 'smooth'),
    });
  }

  syncActiveToViewport() {
    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let nextIndex = this.activeIndex;
    let minDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const distance = Math.abs(trackCenter - slideCenter);

      if (distance < minDistance) {
        minDistance = distance;
        nextIndex = index;
      }
    });

    this.setActive(nextIndex);
  }

  setActive(index) {
    this.activeIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index);
    });

    if (this.dots) {
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    this.syncBackground(index);
  }

  syncBackground(index) {
    if (!this.backgroundImage) return;

    const slideImage = this.slides[index]?.querySelector('.wham-card__image');
    if (!slideImage) return;

    const nextSrc = slideImage.currentSrc || slideImage.getAttribute('src');
    if (!nextSrc) return;

    this.backgroundImage.src = nextSrc;

    const nextSrcset = slideImage.getAttribute('srcset');
    if (nextSrcset) {
      this.backgroundImage.srcset = nextSrcset;
    } else {
      this.backgroundImage.removeAttribute('srcset');
    }
  }
}

document.querySelectorAll('[data-wham-home]').forEach((root) => {
  new WhamHome(root);
});
